import type { ConnectorProfile } from '@/store/auth-store'

// A connector can register on the mobile app, which collects far less than the web
// platform needs, then sign in here. Rather than letting them wander into screens that
// will refuse to work, the gate names exactly what is outstanding.
//
// Requirements differ by entity type. A firm is paid as a business, so its company PAN
// and GST registration are what matter. TDS is reported against the PAN of whoever we
// pay, which for a partnership, LLP or company is the firm's own PAN, so a firm is
// deliberately not asked for a personal PAN. GST is mandatory for firms: a business
// without it belongs on the individual path.

export interface OnboardingRequirement {
  key: string
  label: string
  detail: string
  done: boolean
}

export function onboardingRequirements(profile: ConnectorProfile): OnboardingRequirement[] {
  const items: OnboardingRequirement[] = []

  if (profile.isFirm) {
    items.push({
      key: 'firm-type',
      label: 'Firm type',
      detail: 'Tell us how the firm is constituted.',
      done: Boolean(profile.firmType),
    })
    items.push({
      key: 'firm-pan',
      label: 'Company PAN',
      detail: 'Required so tax deducted at source is reported against the firm.',
      done: profile.firmPanVerified,
    })
    items.push({
      key: 'gstin',
      label: 'GST registration',
      detail: 'Required for firms. GST is added to your invoices once verified.',
      done: profile.gstinVerified,
    })
  } else {
    items.push({
      key: 'pan',
      label: 'PAN',
      detail: 'Required so tax deducted at source is reported against you.',
      done: profile.panVerified,
    })
  }

  items.push({
    key: 'bank',
    label: 'Bank account',
    detail: 'Where PSN Fintech sends your payouts. The name must match your PAN.',
    done: profile.bankVerified,
  })

  return items
}

export function onboardingComplete(profile: ConnectorProfile): boolean {
  return onboardingRequirements(profile).every((item) => item.done)
}

export function outstandingCount(profile: ConnectorProfile): number {
  return onboardingRequirements(profile).filter((item) => !item.done).length
}
